import React from 'react';
import { FiCheck, FiClock, FiUser, FiCalendar, FiAlertCircle, FiBookOpen, FiCheckSquare } from 'react-icons/fi';
import { formatDateForDisplay, formatTimeTo12Hour } from '../../utils/datetime/datetime-comprehensive';
import useAttendanceCheckIn from './useAttendanceCheckIn';
import { Container, CheckInCard, Header, Title, SessionInfo, SessionTitle, SessionDetails, SessionDetail, CheckInSection, CheckInButtons, CheckInButton, StatusMessage, StatusMessageText, LoadingSpinner, EmptyState, EmptyIcon, EmptyTitle, EmptyDescription, TimeWindowInfo, TimeWindowDetail, TimeWindowRestricted, TimeWindowRestrictedMessage, TimeWindowRestrictedMessageText } from './AttendanceCheckIn.styles';

function AttendanceCheckIn() {
  const {
    user,
    authLoading,
    navigate,
    session,
    loading,
    error,
    success,
    attendanceStatus,
    checkingIn,
    getTimeWindowStatus,
    getStatusMessage,
    getStatusColor,
    handleCheckIn,
  } = useAttendanceCheckIn();

  // Don't render anything if not authenticated (will redirect)
  if (!user && !authLoading) {
    console.log('🔍 No user and not loading, returning null');
    return null;
  }

  if (loading || authLoading) {
    return (
      <Container>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner />
            <p>Loading attendance session...</p>
          </div>
        </CheckInCard>
      </Container>
    );
  }

  if (error && !session) {
    return (
      <Container style={{ alignItems: 'flex-start', paddingTop: '1.25rem' }}>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ maxWidth: '860px', padding: '2.25rem 1.75rem' }}
        >
          <EmptyState>
            <EmptyIcon>
              <FiAlertCircle />
            </EmptyIcon>
            <EmptyTitle>Session Not Found</EmptyTitle>
            <EmptyDescription>
              The attendance session you're looking for doesn't exist or you don't have access to it.
            </EmptyDescription>
            <CheckInButton 
              className="primary" 
              onClick={() => navigate('/attendance')}
              style={{ marginTop: '1.8rem', minWidth: '220px' }}
            >
              <FiCheckSquare size={16} />
              Go to My Attendance
            </CheckInButton>
          </EmptyState>
        </CheckInCard>
      </Container>
    );
  }

  return (
    <Container>
      <CheckInCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Header>
          <Title>
            <FiCheckSquare size={24} />
            Attendance Check-In
          </Title>
        </Header>

        {session && (
          <SessionInfo>
            <SessionTitle>{session.title || `${session.course_name} - ${session.course_code}`}</SessionTitle>
            <SessionDetails>
              <SessionDetail>
                <FiCalendar size={16} />
                {formatDateForDisplay(session.date, 'M/d/yyyy')}
              </SessionDetail>
              <SessionDetail>
                <FiUser size={16} />
                {session.instructor_first_name} {session.instructor_last_name}
              </SessionDetail>
              {(session.course_name || session.elective_name || session.course_code) && (
                <SessionDetail>
                  <FiBookOpen size={16} />
                  {session.course_name || session.elective_name || session.course_code}
                </SessionDetail>
              )}
              <SessionDetail>
                <FiClock size={16} />
                {(() => {
                  // Use check-in times if available, otherwise fall back to session times
                  const startTime = session.checkin_start_time || session.start_time;
                  const endTime = session.checkin_end_time || session.end_time;
                  
                  if (startTime && endTime) {
                    return `Check-In Window ${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
                  }
                  
                  return 'No time restrictions';
                })()}
              </SessionDetail>
            </SessionDetails>
          </SessionInfo>
        )}

        {session && getTimeWindowStatus() && !error && (
          <>
            {getTimeWindowStatus().type === 'available' ? (
              <TimeWindowInfo>
                <TimeWindowDetail>
                  <FiCheck size={14} />
                  Check In is available now
                </TimeWindowDetail>
              </TimeWindowInfo>
            ) : (
              <TimeWindowRestricted>
                <TimeWindowRestrictedMessage>
                  <FiAlertCircle size={16} />
                  <TimeWindowRestrictedMessageText>{getTimeWindowStatus().message}</TimeWindowRestrictedMessageText>
                </TimeWindowRestrictedMessage>
              </TimeWindowRestricted>
            )}
          </>
        )}

        {error && (
          <StatusMessage className="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiAlertCircle size={16} />
            <StatusMessageText>{error}</StatusMessageText>
          </StatusMessage>
        )}

        {success && (
          <StatusMessage className="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiCheck size={16} />
            <StatusMessageText>{success}</StatusMessageText>
          </StatusMessage>
        )}

        {attendanceStatus && getStatusMessage() && !success && (
          <StatusMessage className={getStatusColor()} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {getStatusMessage()}
          </StatusMessage>
        )}

        <CheckInSection>
          <CheckInButtons>
            {(!attendanceStatus || attendanceStatus === 'absent') && (
              <CheckInButton 
                className="successSolid"
                onClick={() => handleCheckIn('present')}
                disabled={checkingIn}
                style={{ minWidth: '220px' }}
              >
                {checkingIn ? <LoadingSpinner /> : <FiCheck size={20} />}
                Check In as Present
              </CheckInButton>
            )}

            <CheckInButton 
              className="primary"
              onClick={() => navigate('/attendance')}
              style={{ minWidth: '220px' }}
            >
              Back to My Attendance
            </CheckInButton>
          </CheckInButtons>
        </CheckInSection>
      </CheckInCard>
    </Container>
  );
}

export default AttendanceCheckIn;
